
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { notify } from '@/utils/notification';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface Registration {
  id: string;
  event_id: string;
  client_id: string;
  status: string;
  payment_status: string;
  registered_at: string;
  notes: string | null;
  client_first_name?: string;
  client_last_name?: string;
}

interface RegistrationListProps {
  eventId: string;
  onBack: () => void;
}

const RegistrationList: React.FC<RegistrationListProps> = ({ eventId, onBack }) => {
  const { t } = useTranslation('dashboard');
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('group_event_registrations')
        .select('*')
        .eq('event_id', eventId)
        .order('registered_at', { ascending: true });

      if (error) throw error;

      // Fetch client names
      const clientIds = (data || []).map(r => r.client_id);
      let clientMap: Record<string, { first_name: string; last_name: string }> = {};
      if (clientIds.length > 0) {
        const { data: clients } = await supabase
          .from('clients')
          .select('id, first_name, last_name')
          .in('id', clientIds);
        if (clients) {
          clients.forEach(c => { clientMap[c.id] = c; });
        }
      }

      setRegistrations((data || []).map(r => ({
        ...r,
        client_first_name: clientMap[r.client_id]?.first_name || '',
        client_last_name: clientMap[r.client_id]?.last_name || '',
      })));
    } catch (err) {
      console.error('Failed to fetch registrations:', err);
      notify.error(t('error'), t('events.registrations.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegistrations(); }, [eventId]);

  const handleCheckIn = async (regId: string) => {
    const { error } = await supabase
      .from('group_event_registrations')
      .update({ status: 'attended' })
      .eq('id', regId);
    if (error) {
      notify.error(t('error'), t('events.registrations.checkInFailed'));
    } else {
      notify.success(t('events.registrations.checkedIn'));
      fetchRegistrations();
    }
  };

  const statusColors: Record<string, string> = {
    registered: 'default',
    attended: 'default',
    cancelled: 'destructive',
    waitlisted: 'secondary',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-xl font-semibold">{t('events.registrations.title')}</h2>
        <Badge variant="outline">{registrations.length} {t('events.registered')}</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : registrations.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{t('events.registrations.noRegistrations')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">#</th>
                <th className="text-left p-3 font-medium">{t('events.registrations.name')}</th>
                <th className="text-left p-3 font-medium">{t('events.registrations.registeredAt')}</th>
                <th className="text-left p-3 font-medium">{t('events.registrations.status')}</th>
                <th className="text-left p-3 font-medium">{t('events.registrations.payment')}</th>
                <th className="text-left p-3 font-medium">{t('events.registrations.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((reg, idx) => (
                <tr key={reg.id} className="border-t">
                  <td className="p-3">{idx + 1}</td>
                  <td className="p-3">{reg.client_first_name} {reg.client_last_name}</td>
                  <td className="p-3">{format(new Date(reg.registered_at), 'MMM d, HH:mm')}</td>
                  <td className="p-3">
                    <Badge variant={statusColors[reg.status] as any || 'default'}>{reg.status}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge variant={reg.payment_status === 'paid' ? 'default' : 'secondary'}>{reg.payment_status}</Badge>
                  </td>
                  <td className="p-3">
                    {reg.status === 'registered' && (
                      <Button size="sm" variant="outline" onClick={() => handleCheckIn(reg.id)}>
                        <CheckCircle className="h-3 w-3 mr-1" /> {t('events.registrations.checkIn')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RegistrationList;
